import { useState, type FormEvent } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { useAnnee } from '../hooks/useAnnee';
import { BandeauAnneeFigee } from '../components/SelecteurAnnee';
import { useDelai, useRessource } from '../hooks/useRessource';
import { CommunicationGroupee } from '../components/CommunicationGroupee';
import { ErreurApi, api } from '../lib/apiClient';
import {
  Badge,
  Champ,
  Chargement,
  EnTetePage,
  Erreur,
  Infobulle,
  Modale,
  Tableau,
  type Colonne,
} from '../components/Elements';
import { PiedFormulaire } from './ReferentielsPages';
import { BandeauMetriques } from '../components/BandeauMetriques';
import { BarreRecherche, type FiltreActif } from '../components/BarreRecherche';
import { FormulaireTransfert } from '../components/FormulaireTransfert';
import { ChoixSeance } from '../components/ChoixSeance';
import { MenuActions } from '../components/MenuActions';
import { ChoixActivite } from '../components/ChoixActivite';
import { ChoixCreneau } from '../components/ChoixCreneau';
import { ChampReductions } from '../components/ChampReductions';
import { decrireReductions, totalReductions, type SaisieLigneReduction } from '../lib/reductions';
import { CLASSE_SURVOL, useListeClavier } from '../hooks/useListeClavier';
import { exporterTableau, montantExport } from '../lib/export';
import {
  enCentimes,
  enEuros,
  formaterDate,
  formaterHeure,
  formaterMontant,
  formaterPlage,
  libelleJour,
  libelleStatut,
} from '../lib/format';
import type {
  Activite,
  Creneau,
  Inscription,
  Lieu,
  Personne,
  Personnel,
  Prorata,
  SimulationFinAnticipee,
  SyntheseInscriptions,
  TypeReduction,
} from '../types';

export function InscriptionsPage() {
  const { peut, entiteActive } = useAuth();
  const [filtres, setFiltres] = useState({
    activiteId: '',
    professeurId: '',
    lieuId: '',
    statut: '',
    sansCreneau: false,
    // Un type de reduction, ou « non_detaillee » (2026-10-12).
    reduction: '',
  });
  const [saisie, setSaisie] = useState('');
  // La frappe ne declenche pas une requete par caractere : meme delai que
  // sur l'ecran Adherents, pour que les trois listes se comportent pareil.
  const recherche = useDelai(saisie);
  const [modaleOuverte, setModaleOuverte] = useState(false);
  const [finAnticipee, setFinAnticipee] = useState<Inscription | null>(null);
  const [transfert, setTransfert] = useState<Inscription | null>(null);
  // « Ecrire a la selection » (2026-10-08) : les ADHERENTS des lignes
  // affichees, chacun une fois, geles a l'ouverture.
  const [ecrireA, setEcrireA] = useState<string[] | null>(null);

  // Le plafond du serveur, transmis et SIGNALE (2026-10-08) : la liste se
  // coupait a 500 sans le dire — et une communication groupee serait
  // partie a une liste tronquee. 381 inscriptions en 2025-26.
  const LIMITE = 500;

  // Le filtre par creneau vit dans l'URL, pas dans l'etat : c'est le lien
  // qu'offre une tuile du planning, et la page reste partageable
  // — meme procede que le filtre par salarie de l'ecran Contrats.
  const [parametres, setParametres] = useSearchParams();
  const creneauFiltre = parametres.get('creneauId') ?? '';

  const retirerParametre = (nom: string) => {
    const suite = new URLSearchParams(parametres);
    suite.delete(nom);
    setParametres(suite, { replace: true });
  };

  const cle = entiteActive?.id ?? '';
  // L'annee vient du BANDEAU, partagee par toute l'application. Cet
  // ecran est un ecran de SAISIE : sur une annee clouturee il affiche
  // ce qu'elle contient, mais n'y laisse plus rien creer (RG-27). Il
  // suit donc le bandeau plutot que de basculer en silence sur une
  // autre annee — c'est ce que la centralisation doit supprimer.
  const annee = useAnnee();
  const anneeAffichee = annee.annee;
  const anneeFermee = anneeAffichee !== null && anneeAffichee.statut === 'cloturee';

  // L'annee voyage avec le creneau : une tuile de planning 2026-27 doit
  // amener la liste sur 2026-27, pas sur l'annee du bandeau. On ne
  // l'applique qu'une fois.
  const anneeUrl = parametres.get('anneeId') ?? '';
  const [anneeAppliquee, setAnneeAppliquee] = useState('');
  if (anneeUrl && anneeUrl !== anneeAppliquee && annee.annees.some((a) => a.id === anneeUrl)) {
    setAnneeAppliquee(anneeUrl);
    annee.choisir(anneeUrl);
  }

  // Le libelle de la pastille : on ne charge la grille que si un creneau
  // est effectivement filtre.
  const creneauxAnnee = useRessource(
    () =>
      creneauFiltre && anneeAffichee
        ? api.get<Creneau[]>('/api/creneaux', { anneeId: anneeAffichee.id })
        : Promise.resolve([]),
    [cle, creneauFiltre, anneeAffichee?.id],
  );
  const creneauCourant = (creneauxAnnee.donnees ?? []).find((c) => c.id === creneauFiltre) ?? null;

  // Referentiel annuel : les activites proposees sont celles de l'annee
  // affichee — celles de 2026-27 pour une saisie 2026-27.
  const activites = useRessource(
    () =>
      anneeAffichee
        ? api.get<Activite[]>('/api/activites', { anneeId: anneeAffichee.id })
        : Promise.resolve([]),
    [cle, anneeAffichee?.id],
  );
  // La grille complete de l'annee, pour le choix d'un creneau de
  // destination. Elle ne se charge QUE modale ouverte : 166 creneaux ne
  // servent a rien tant qu'on n'a pas clique « Transferer ».
  const creneauxTransfert = useRessource(
    () =>
      transfert && anneeAffichee
        ? api.get<Creneau[]>('/api/creneaux', { anneeId: anneeAffichee.id })
        : Promise.resolve([]),
    [cle, transfert?.id, anneeAffichee?.id],
  );

  const professeurs = useRessource(() => api.get<Personnel[]>('/api/personnel'), [cle]);
  const lieux = useRessource(() => api.get<Lieu[]>('/api/lieux'), [cle]);
  // Les types de reduction de l'annee, desactives compris : une ligne
  // posee sur un type desactive depuis doit rester filtrable.
  const typesReduction = useRessource(
    () =>
      anneeAffichee
        ? api.get<TypeReduction[]>('/api/types-reduction', {
            anneeId: anneeAffichee.id,
            inclureInactifs: true,
          })
        : Promise.resolve([]),
    [cle, anneeAffichee?.id],
  );

  const inscriptions = useRessource(
    () =>
      anneeAffichee
        ? api.get<Inscription[]>('/api/inscriptions', {
            anneeId: anneeAffichee.id,
            recherche: recherche || undefined,
            activiteId: filtres.activiteId || undefined,
            professeurId: filtres.professeurId || undefined,
            lieuId: filtres.lieuId || undefined,
            statut: filtres.statut || undefined,
            sansCreneau: filtres.sansCreneau || undefined,
            typeReductionId:
              filtres.reduction && filtres.reduction !== 'non_detaillee'
                ? filtres.reduction
                : undefined,
            reductionNonDetaillee: filtres.reduction === 'non_detaillee' || undefined,
            creneauId: creneauFiltre || undefined,
            limite: LIMITE,
          })
        : Promise.resolve([]),
    [cle, anneeAffichee?.id, recherche, JSON.stringify(filtres), creneauFiltre],
  );

  const colonnes: Colonne<Inscription>[] = [
    {
      cle: 'adherent',
      entete: 'Adhérent',
      rendu: (i) => (
        <Link to={`/adherents/${i.personne_id}`} className="font-medium hover:underline">
          {i.personne_nom.toUpperCase()} {i.personne_prenom}
        </Link>
      ),
    },
    {
      cle: 'activite',
      entete: 'Activité',
      /**
       * La colonne Statut a ete retiree. Le statut lui-meme se filtre et
       * n'a pas besoin d'une colonne, mais elle portait deux marqueurs
       * qui, eux, ne se deduisent de rien : la date de fin anticipee et
       * le forfait d'essai. Ils descendent ici plutot que de disparaitre.
       */
      rendu: (i) => (
        <span className="space-y-0.5">
          <span className="flex items-center gap-2">
            {i.activite_libelle}
            {i.pupitre && <Badge>{i.pupitre}</Badge>}
          </span>
          {i.date_fin_anticipee && (
            <span className="block text-xs text-encre-400">
              fin le {formaterDate(i.date_fin_anticipee)}
            </span>
          )}
          {/* L'essai se lit par son ETAT (2026-10-08) : en cours en ambre,
              echu en rouge — la fin est passee sans confirmation —, et
              plus rien une fois confirme. */}
          {i.essai_statut === 'en_cours' && (
            <span className="block text-xs text-amber-700">
              essai{i.date_fin_essai ? ` → ${formaterDate(i.date_fin_essai)}` : ''}
            </span>
          )}
          {i.essai_statut === 'echu' && (
            <span className="block text-xs font-medium text-red-700">
              essai terminé{i.date_fin_essai ? ` le ${formaterDate(i.date_fin_essai)}` : ''}
            </span>
          )}
        </span>
      ),
    },
    {
      cle: 'creneau',
      entete: 'Créneau',
      // Le professeur n'a plus sa colonne : il tient sur la ligne du
      // creneau, ou il se lit comme ce qu'il est — une coordonnee du
      // cours, au meme titre que le jour et la salle.
      rendu: (i) =>
        i.creneau_id ? (
          <span className="text-encre-600">
            {libelleJour(i.jour)} {formaterPlage(i.heure_debut, i.heure_fin)}
            {i.lieu_code && <span className="ml-1 text-encre-400">· {i.lieu_code}</span>}
            {i.professeur_nom && <span className="ml-1 text-encre-400">· {i.professeur_nom}</span>}
          </span>
        ) : (
          <Badge ton="alerte">Sans créneau</Badge>
        ),
    },
    {
      cle: 'montant',
      entete: 'Montant dû',
      classe: 'text-right',
      // La reduction ne s'ecrit plus en clair a cote du montant : deux
      // nombres dans une colonne se lisent mal, et celui qui compte est
      // le montant du. Une pastille signale qu'il en contient une, et
      // l'infobulle donne le detail a qui le cherche.
      rendu: (i) => (
        <span className="flex items-center justify-end gap-1.5">
          {i.reduction > 0 && (
            <Infobulle texte={decrireReductions(i)}>
              {/* Ambre quand une part n'est detaillee par aucun type (2026-10-12). */}
              <Badge ton={i.reduction_non_detaillee > 0 ? 'alerte' : 'neutre'}>réduit</Badge>
            </Infobulle>
          )}
          <span className="tabulaire">{formaterMontant(i.montant_du)}</span>
        </span>
      ),
    },
    {
      cle: 'restant',
      // « Ecart » (2026-10-07) : le signe dit le sens — rouge, il reste a
      // payer ; vert, on a trop recu. « Restant du » ne couvrait que le
      // premier cas.
      entete: 'Écart',
      classe: 'text-right',
      /**
       * Ce qui reste a encaisser sur CETTE inscription : le du, moins ce
       * que les reglements lui allouent. Les deux membres existent deja
       * dans la reponse, rien n'est demande au serveur.
       *
       * Meme convention que la fiche adherent : rouge quand il reste a
       * payer, vert quand on a trop recu, et un tiret quand c'est solde
       * — « 0,00 € » en rouge se lirait comme une alerte.
       */
      rendu: (i) => {
        // Sur le montant EXIGIBLE : le forfait tant que l'essai n'est pas
        // confirme, le du sinon (2026-10-08).
        const reste = i.montant_exigible - i.montant_regle;
        if (reste === 0) return <span className="text-encre-300">—</span>;
        return (
          <span className={`tabulaire ${reste > 0 ? 'text-red-800' : 'text-emerald-700'}`}>
            {formaterMontant(Math.abs(reste))}
          </span>
        );
      },
    },
    {
      cle: 'actions',
      entete: '',
      classe: 'text-right',
      // Deux gestes distincts, et deux permissions distinctes. Transferer
      // n'est pas modifier : quand l'activite change, la ventilation des
      // reglements suit l'eleve — d'ou `inscription.transferer`, que seuls
      // la Direction et le super admin portent.
      rendu: (i) =>
        i.statut === 'actif' ? (
          <span className="flex justify-end gap-3 whitespace-nowrap">
            {peut('inscription.modifier') && (i.essai_statut === 'en_cours' || i.essai_statut === 'echu') && (
              <button
                type="button"
                className={`text-sm underline hover:text-encre-900 ${i.essai_statut === 'echu' ? 'font-medium text-red-700' : 'text-encre-500'}`}
                onClick={() => void confirmerEssai(i)}
              >
                Confirmer l’essai
              </button>
            )}
            {peut('inscription.transferer') && (
              <button
                type="button"
                className="text-sm text-encre-500 underline hover:text-encre-900"
                onClick={() => setTransfert(i)}
              >
                Transférer
              </button>
            )}
            {peut('inscription.cloturer') && (
              <button
                type="button"
                className="text-sm text-encre-500 underline hover:text-encre-900"
                onClick={() => setFinAnticipee(i)}
              >
                Fin anticipée
              </button>
            )}
          </span>
        ) : null,
    },
  ];

  const total = (inscriptions.donnees ?? []).reduce((somme, i) => somme + i.montant_du, 0);
  const actives = (inscriptions.donnees ?? []).filter((i) => i.statut === 'actif').length;

  const synthese = useRessource(
    () =>
      anneeAffichee
        ? api.get<SyntheseInscriptions>('/api/inscriptions/synthese', { anneeId: anneeAffichee.id })
        : Promise.resolve(null),
    [cle, anneeAffichee?.id, JSON.stringify(filtres)],
  );

  // CONFIRMER L'ESSAI (2026-10-08) : l'eleve poursuit, le plein tarif
  // s'applique. La liste et les tuiles se rechargent.
  const confirmerEssai = async (i: Inscription) => {
    if (!window.confirm(`Confirmer que ${i.personne_prenom} ${i.personne_nom} poursuit « ${i.activite_libelle} » au plein tarif ?`)) return;
    await api.post(`/api/inscriptions/${i.id}/confirmer-essai`, {});
    inscriptions.recharger();
    synthese.recharger();
  };

  const filtresActifs: FiltreActif[] = [
    recherche && {
      cle: 'recherche',
      libelle: `« ${recherche} »`,
      onRetirer: () => setSaisie(''),
    },
    filtres.activiteId && {
      cle: 'activite',
      libelle:
        (activites.donnees ?? []).find((a) => a.id === filtres.activiteId)?.libelle ?? 'Activité',
      onRetirer: () => setFiltres({ ...filtres, activiteId: '' }),
    },
    filtres.professeurId && {
      cle: 'professeur',
      libelle:
        (professeurs.donnees ?? []).find((p) => p.id === filtres.professeurId)?.prenom_usuel ??
        'Professeur',
      onRetirer: () => setFiltres({ ...filtres, professeurId: '' }),
    },
    filtres.lieuId && {
      cle: 'lieu',
      libelle: (lieux.donnees ?? []).find((l) => l.id === filtres.lieuId)?.code ?? 'Lieu',
      onRetirer: () => setFiltres({ ...filtres, lieuId: '' }),
    },
    filtres.statut && {
      cle: 'statut',
      libelle: libelleStatut(filtres.statut as Inscription['statut']),
      onRetirer: () => setFiltres({ ...filtres, statut: '' }),
    },
    filtres.sansCreneau && {
      cle: 'sansCreneau',
      libelle: 'Sans créneau',
      onRetirer: () => setFiltres({ ...filtres, sansCreneau: false }),
    },
    filtres.reduction && {
      cle: 'reduction',
      libelle:
        filtres.reduction === 'non_detaillee'
          ? 'Réduction non détaillée'
          : (typesReduction.donnees ?? []).find((t) => t.id === filtres.reduction)?.libelle ??
            'Réduction',
      onRetirer: () => setFiltres({ ...filtres, reduction: '' }),
    },
    creneauFiltre && {
      cle: 'creneau',
      libelle: creneauCourant
        ? `${libelleJour(creneauCourant.jour)} ${formaterHeure(creneauCourant.heure_debut)} · ${creneauCourant.activite_libelle}`
        : 'Créneau',
      onRetirer: () => retirerParametre('creneauId'),
    },
  ].filter(Boolean) as FiltreActif[];

  const toutEffacer = () => {
    setSaisie('');
    setFiltres({
      activiteId: '',
      professeurId: '',
      lieuId: '',
      statut: '',
      reduction: '',
      sansCreneau: false,
    });
    retirerParametre('creneauId');
  };

  if (annee.chargement) return <Chargement />;

  if (!anneeAffichee) {
    return (
      <>
        <EnTetePage titre="Inscriptions" />
        <div className="carte p-6 text-sm text-encre-500">
          Aucune année scolaire ouverte (active ou en préparation) sur cette entité. Ouvrez-en une
          depuis{' '}
          <Link to="/annees" className="underline">
            Référentiels → Années scolaires
          </Link>
          .
        </div>
      </>
    );
  }

  return (
    <>
      <EnTetePage
        titre="Inscriptions"
        description="Une personne peut avoir plusieurs inscriptions à une même activité — deux cours de piano par semaine, par exemple — mais pas deux fois le même créneau."
        actions={
          <div className="flex items-center gap-3">
            {peut('inscription.creer') && !anneeFermee && (
              <button
                type="button"
                className="bouton-principal"
                onClick={() => setModaleOuverte(true)}
              >
                Nouvelle inscription
              </button>
            )}
          </div>
        }
      />

      {/* RG-27 : le serveur refuse d'ecrire sur une annee clouturee.
          L'ecran le dit et retire le bouton, plutot que de laisser la
          saisie echouer a l'enregistrement. */}
      {anneeFermee && <BandeauAnneeFigee annee={anneeAffichee} />}

      {synthese.donnees && (
        <BandeauMetriques
          metriques={[
            {
              libelle: 'Inscriptions actives',
              valeur: String(synthese.donnees.actives),
              detail: `${synthese.donnees.adherents} adhérents · ${synthese.donnees.arrets} arrêts`,
            },
            {
              libelle: 'Chiffre d’affaires',
              valeur: formaterMontant(synthese.donnees.chiffre_affaires),
              detail: `${formaterMontant(synthese.donnees.reste_a_encaisser)} restant à encaisser`,
            },
            {
              libelle: 'Réductions',
              valeur: formaterMontant(synthese.donnees.reductions),
              detail: `sur ${synthese.donnees.lignes_reduites} inscriptions`,
            },
            {
              libelle: 'Sans créneau',
              valeur: String(synthese.donnees.sans_creneau),
              detail: 'inscriptions actives',
              ton: 'alerte',
              action: () => setFiltres({ ...filtres, sansCreneau: true, statut: 'actif' }),
            },
          ]}
        />
      )}

      {anneeFermee && creneauFiltre && (
        <p className="mb-3 rounded-md border border-amber-200 bg-amber-50 px-4 py-2 text-sm text-amber-800">
          Ce créneau appartient à{' '}
          {annee.annees.find((a) => a.id === anneeUrl)?.libelle ?? 'une année clôturée'}, que cet
          écran de saisie ne propose pas. Les inscriptions affichées sont celles de{' '}
          {anneeAffichee?.libelle ?? 'l’année en cours'}.
        </p>
      )}

      <BarreRecherche
        recherche={saisie}
        onChercher={setSaisie}
        placeholder="Nom, prénom ou activité…"
        actifs={filtresActifs}
        onToutEffacer={toutEffacer}
        resume={
          inscriptions.donnees ? (
            <>
              <span className="tabulaire font-medium text-encre-800">
                {inscriptions.donnees.length}
              </span>{' '}
              inscription(s){synthese.donnees ? ` sur ${synthese.donnees.inscriptions_total}` : ''}{' '}
              — <span className="tabulaire">{formaterMontant(total)}</span> dus
              {actives !== inscriptions.donnees.length && `, dont ${actives} active(s)`}
              {inscriptions.donnees.length === LIMITE && (
                <span className="ml-2 text-amber-700">
                  — liste tronquée à {LIMITE}, affinez la recherche
                </span>
              )}
            </>
          ) : null
        }
        onEcrire={
          inscriptions.donnees?.length && peut('message.envoyer')
            ? () => setEcrireA([...new Set(inscriptions.donnees!.map((i) => i.personne_id))])
            : undefined
        }
        onExporter={
          inscriptions.donnees?.length
            ? () =>
                exporterTableau(
                  'inscriptions',
                  [
                    'Nom',
                    'Prénom',
                    'Activité',
                    'Jour',
                    'Heure',
                    'Professeur',
                    'Lieu',
                    'Dû',
                    'Réduction',
                    'Détail des réductions',
                    'Statut',
                  ],
                  inscriptions.donnees!.map((i) => [
                    i.personne_nom,
                    i.personne_prenom,
                    i.activite_libelle,
                    i.jour ? libelleJour(i.jour) : '',
                    i.heure_debut ? formaterHeure(i.heure_debut) : '',
                    i.professeur_nom ?? '',
                    i.lieu_code ?? '',
                    montantExport(i.montant_du),
                    montantExport(i.reduction),
                    i.reduction > 0 ? decrireReductions(i) : '',
                    libelleStatut(i.statut),
                  ]),
                )
            : undefined
        }
        filtres={
          <>
            <select
              className="champ w-44"
              value={filtres.activiteId}
              onChange={(e) => setFiltres({ ...filtres, activiteId: e.target.value })}
            >
              <option value="">Toutes les activités</option>
              {(activites.donnees ?? []).map((a) => (
                <option key={a.id} value={a.id}>
                  {a.libelle}
                </option>
              ))}
            </select>

            <select
              className="champ w-44"
              value={filtres.professeurId}
              onChange={(e) => setFiltres({ ...filtres, professeurId: e.target.value })}
            >
              <option value="">Tous les professeurs</option>
              {(professeurs.donnees ?? []).map((p) => (
                <option key={p.id} value={p.id}>
                  {p.prenom_usuel} {p.nom ?? ''}
                </option>
              ))}
            </select>

            <select
              className="champ w-44"
              value={filtres.lieuId}
              onChange={(e) => setFiltres({ ...filtres, lieuId: e.target.value })}
            >
              <option value="">Tous les lieux</option>
              {(lieux.donnees ?? []).map((l) => (
                <option key={l.id} value={l.id}>
                  {l.code}
                </option>
              ))}
            </select>

            <select
              className="champ w-44"
              value={filtres.statut}
              onChange={(e) => setFiltres({ ...filtres, statut: e.target.value })}
            >
              <option value="">Tous les statuts</option>
              <option value="actif">Actif</option>
              <option value="inactif">Inactif</option>
              <option value="fin_anticipee">Fin anticipée</option>
            </select>

            <select
              className="champ w-44"
              value={filtres.reduction}
              onChange={(e) => setFiltres({ ...filtres, reduction: e.target.value })}
            >
              <option value="">Toutes les réductions</option>
              {(typesReduction.donnees ?? []).map((t) => (
                <option key={t.id} value={t.id}>
                  {t.libelle}
                </option>
              ))}
              {/* La part que les types n'expliquent pas : les inscriptions
                  d'avant le referentiel, a qualifier a l'occasion. */}
              <option value="non_detaillee">Non détaillée</option>
            </select>

            {/* La case « Sans creneau » a ete retiree : une case a cocher
                au bout d'une rangee de listes deroulantes cassait
                l'alignement, et le filtre reste accessible — la quatrieme
                tuile le pose, la pastille le retire. Meme grammaire que
                l'ecran Adherents. */}
          </>
        }
      />

      {inscriptions.erreur && (
        <Erreur message={inscriptions.erreur} onReessayer={inscriptions.recharger} />
      )}
      {inscriptions.chargement ? (
        <Chargement />
      ) : (
        <div className="carte">
          <Tableau
            colonnes={colonnes}
            lignes={inscriptions.donnees ?? []}
            cleDe={(i) => i.id}
            messageVide="Aucune inscription ne correspond."
          />
        </div>
      )}

      <CommunicationGroupee
        ouverte={ecrireA !== null}
        onFermer={() => setEcrireA(null)}
        source={ecrireA ? { personneIds: ecrireA } : null}
        origine={
          ecrireA
            ? `Les ${ecrireA.length} adhérent(s) des ${inscriptions.donnees?.length ?? 0} inscription(s) affichée(s), et leurs contacts — filtres : ${
                filtresActifs.map((f) => f.libelle).join(', ') || 'aucun'
              }, année ${anneeAffichee.libelle}.`
            : ''
        }
      />

      <FormulaireInscription
        onTransferer={setTransfert}
        onFinAnticipee={setFinAnticipee}
        onConfirmerEssai={(i) => void confirmerEssai(i)}
        onRouvrir={async (i) => {
          await api.post(`/api/inscriptions/${i.id}/reouverture`, {});
          setModaleOuverte(false);
          inscriptions.recharger();
        }}
        ouverte={modaleOuverte}
        anneeId={anneeAffichee.id}
        activites={activites.donnees ?? []}
        onFermer={() => setModaleOuverte(false)}
        onEnregistre={() => {
          setModaleOuverte(false);
          inscriptions.recharger();
        }}
      />

      <FormulaireTransfert
        inscription={transfert}
        creneaux={creneauxTransfert.donnees ?? []}
        activites={activites.donnees ?? []}
        onFermer={() => setTransfert(null)}
        onEnregistre={() => {
          setTransfert(null);
          inscriptions.recharger();
        }}
      />

      <FormulaireFinAnticipee
        inscription={finAnticipee}
        onFermer={() => setFinAnticipee(null)}
        onEnregistre={() => {
          setFinAnticipee(null);
          inscriptions.recharger();
        }}
      />
    </>
  );
}

// --- Création ---------------------------------------------------------

/** Fleches croisees : on echange un creneau contre un autre. */
function IconeTransfert() {
  return (
    <svg viewBox="0 0 16 16" className="h-4 w-4" fill="currentColor" aria-hidden>
      <path d="M10.5 1.8 14 5.3l-3.5 3.5-1.06-1.06 1.7-1.7H2v-1.5h9.13l-1.7-1.7L10.5 1.8ZM5.5 7.2 6.56 8.26l-1.7 1.7H14v1.5H4.87l1.7 1.7L5.5 14.2 2 10.7l3.5-3.5Z" />
    </svg>
  );
}

/** Fleche qui revient : defaire un arret. */
function IconeRetour() {
  return (
    <svg viewBox="0 0 16 16" className="h-4 w-4" fill="currentColor" aria-hidden>
      <path d="M3.5 4.2V1.9H2v5h5V5.4H4.6a4.6 4.6 0 1 1-1.03 3.9l-1.47.3A6.1 6.1 0 1 0 3.5 4.2Z" />
    </svg>
  );
}

/** Carre plein : l'arret. */
function IconeArret() {
  return (
    <svg viewBox="0 0 16 16" className="h-4 w-4" fill="currentColor" aria-hidden>
      <rect x="3" y="3" width="10" height="10" rx="1.5" />
    </svg>
  );
}

export function FormulaireInscription({
  onTransferer,
  onFinAnticipee,
  onConfirmerEssai,
  onRouvrir,
  ouverte,
  anneeId,
  activites,
  personneImposee,
  inscription,
  onFermer,
  onEnregistre,
}: {
  /**
   * Actes de gestion offerts par le menu de l'en-tete. Le PARENT les
   * porte : c'est lui qui possede les modales et sait fermer celle-ci
   * avant d'ouvrir l'autre. Absents en creation — on ne transfere pas
   * une inscription qui n'existe pas encore.
   */
  onTransferer?: (inscription: Inscription) => void;
  onFinAnticipee?: (inscription: Inscription) => void;
  /** L'eleve poursuit apres son forfait d'essai (2026-10-08). */
  onConfirmerEssai?: (inscription: Inscription) => void;
  /** Super admin seul : defaire une fin anticipee mal saisie. */
  onRouvrir?: (inscription: Inscription) => void;
  ouverte: boolean;
  anneeId: string;
  activites: Activite[];
  /** Depuis une fiche adhérent, la personne est déjà connue. */
  personneImposee?: Personne;
  /** `null` pour une création ; l'inscription à corriger sinon. */
  inscription?: Inscription | null;
  onFermer: () => void;
  onEnregistre: () => void;
}) {
  const [recherche, setRecherche] = useState('');
  const [candidats, setCandidats] = useState<Personne[]>([]);
  const [personne, setPersonne] = useState<Personne | null>(null);
  const [activiteId, setActiviteId] = useState('');
  const [creneauId, setCreneauId] = useState('');
  const [pupitre, setPupitre] = useState('');
  const [montant, setMontant] = useState('');
  // LES REDUCTIONS SONT DES LIGNES TYPEES (2026-10-12) : un type coche, et
  // pour une remise libre son montant et son motif. `nonDetaillee` est la
  // part d'une inscription anterieure qu'aucune ligne n'explique : elle
  // s'affiche et ne se retire qu'explicitement.
  const [lignesReduction, setLignesReduction] = useState<SaisieLigneReduction[]>([]);
  const [nonDetaillee, setNonDetaillee] = useState(0);
  const [debutSaisi, setDebutSaisi] = useState(false);
  const [dateDebut, setDateDebut] = useState('');
  // Montant force par le prorata d'un demarrage en cours d'annee ; nul,
  // le montant est le tarif de l'activite. Voir la garde plus bas.
  const [montantForce, setMontantForce] = useState<number | null>(null);
  const [forfaitEssai, setForfaitEssai] = useState(false);
  const [erreur, setErreur] = useState<string | null>(null);
  const [enCours, setEnCours] = useState(false);
  const [initialisePour, setInitialisePour] = useState<string | null>(null);

  // Les entrees du menu dependent des permissions : le formulaire les
  // interroge lui-meme plutot que de se les faire passer, comme partout
  // ailleurs (RG-35 : une permission nommee, jamais un nom de role).
  const { peut } = useAuth();

  /**
   * UNE INSCRIPTION ARRETEE NE SE MODIFIE PLUS.
   *
   * La fin anticipee fige un montant recalcule au prorata des seances
   * reellement suivies. Rouvrir le montant, le creneau ou l'activite
   * derriere ce calcul le rendrait faux sans que rien ne le signale — et
   * le rapprochement de l'adherent avec lui.
   *
   * L'ecran devient une CONSULTATION. Le menu perd ses deux actes de
   * gestion — une inscription arretee est close, la transferer n'aurait
   * pas de sens et re-declarer une fin anticipee en poserait une seconde
   * par-dessus la premiere — et gagne le seul geste qui vaille encore :
   * REVENIR sur la fin anticipee, pour rattraper une saisie erronee.
   * Reserve au super admin, la permission n'etant accordee a aucun role.
   */
  const arretee = inscription?.statut === 'fin_anticipee';

  // Reinitialisation au rendu, sans useEffect : la cle suit l'objet edite.
  const cle = ouverte ? (inscription?.id ?? 'nouvelle') : null;
  if (cle && cle !== initialisePour) {
    setInitialisePour(cle);
    setActiviteId(inscription?.activite_id ?? '');
    setCreneauId(inscription?.creneau_id ?? '');
    setPupitre(inscription?.pupitre ?? '');
    setMontant(inscription ? enEuros(inscription.montant_base) : '');
    setLignesReduction(
      (inscription?.reductions ?? []).map((l) => ({
        typeReductionId: l.type_reduction_id,
        montant: enEuros(l.montant),
        commentaire: l.commentaire ?? '',
      })),
    );
    setNonDetaillee(inscription?.reduction_non_detaillee ?? 0);
    // La case lit le DRAPEAU, pas la presence d'une date : celle-ci est
    // obligatoire et toujours remplie, elle cochait donc tout le monde.
    setDebutSaisi(Boolean(inscription?.demarrage_differe));
    setMontantForce(null);
    // La date ne se reprend QUE si le demarrage est reellement differe.
    // Sinon le champ reste vide : `date_debut` porte alors le debut
    // normal, une valeur que le serveur a posee et que l'utilisateur n'a
    // pas choisie — la ressortir en saisie la ferait passer pour un
    // choix, et c'est ainsi qu'on a fabrique 572 faux differes.
    setDateDebut(inscription?.demarrage_differe ? (inscription.date_debut ?? '') : '');
    setForfaitEssai(inscription?.forfait_essai ?? false);
    setErreur(null);
  }
  if (!ouverte && initialisePour !== null) setInitialisePour(null);

  const choisie = personneImposee ?? personne;
  const activite = activites.find((a) => a.id === activiteId);
  const estChorale = activite?.type === 'Chorale';

  // Le tarif du referentiel EST le montant (2026-09-07), et le champ ne
  // se modifie plus. Un ecart — prorata, fratrie, cas particulier — passe
  // par la reduction, le seul endroit qui l'explique.
  //
  // PAS D'ETAT INTERMEDIAIRE : en creation, des que l'activite porte un
  // tarif, le montant lui est egal, point. La version precedente
  // memorisait « tarif deja applique pour cette activite » — retirer
  // l'activite vidait le montant, la rechoisir ne le reposait pas, et le
  // champ etant desormais ferme, on se retrouvait avec un montant vide
  // impossible a retaper. Un champ qu'on ne peut pas modifier ne doit
  // jamais pouvoir se vider.
  //
  // Jamais en modification : le montant repris est celui qui a ete
  // convenu, affiche tel quel, grise. Le realigner sur un tarif qui
  // aurait change depuis serait un geste a part entiere.
  //
  // LE PRORATA FORCE LE MONTANT (decision du 2026-09-07). C'est un ecart
  // volontaire a la convention « le prorata en reduction » : ici il
  // remplace le montant — sur le net de reduction depuis le 2026-10-07, a
  // l'euro inferieur depuis le 2026-11-03, plus la reduction — et la
  // reduction n'est pas touchee. `montantForce` porte cette valeur ; nul, le montant
  // est le tarif. Il s'annule des que ce qui l'a produit change —
  // activite, creneau, date, case decochee — sans quoi un prorata calcule
  // pour une autre situation survivrait dans un champ qu'on ne peut pas
  // corriger a la main.
  if (!inscription && activite && activite.tarif !== null) {
    const attendu = enEuros(montantForce ?? activite.tarif);
    if (montant !== attendu) setMontant(attendu);
  }
  // EN MODIFICATION AUSSI (2026-09-30). Le prorata force n'etait pose
  // qu'a la creation : sur une inscription existante, « Appliquer le
  // nouveau tarif proratise » annoncait le montant sans jamais l'ecrire
  // dans le champ, et l'enregistrement renvoyait l'ancien — JOHANN
  // Antoine restait a 960 EUR pour 29 seances sur 30. Tant qu'aucun
  // prorata n'est force, le montant reste celui qui a ete convenu.
  //
  // DECOCHER « Demarrage en cours d'annee » sur une inscription qui
  // l'etait REMONTE le montant au tarif : c'est vraisemblablement une
  // erreur de saisie qu'on corrige, et le prorata part avec la case. Un
  // montant deja au tarif ou au-dessus n'est pas touche — ce n'est pas un
  // prorata qu'on defait.
  if (inscription && activite && activite.tarif !== null) {
    const prorataDefait =
      inscription.demarrage_differe && !debutSaisi && inscription.montant_base < activite.tarif;
    const attendu = enEuros(
      montantForce ?? (prorataDefait ? activite.tarif : inscription.montant_base),
    );
    if (montant !== attendu) setMontant(attendu);
  }

  // Les créneaux dépendent de l'activité : on ne les charge qu'une fois
  // celle-ci choisie, et on les rafraîchit à chaque changement.
  const creneaux = useRessource(
    () =>
      activiteId
        ? api.get<Creneau[]>('/api/creneaux', { anneeId, activiteId })
        : Promise.resolve([]),
    [activiteId, anneeId],
  );

  // On ne propose que les creneaux OU IL RESTE DE LA PLACE. Depuis que la
  // capacite cascade du type vers l'activite puis le creneau, un cours
  // individuel vaut une place : ses creneaux deja pris disparaissent de
  // la liste, et il ne reste que les horaires ou l'on peut inscrire.
  //
  // Exception : en modification, le creneau DEJA rattache reste propose
  // meme complet. Le retirer le remplacerait silencieusement par « sans
  // creneau » au premier enregistrement — l'inscription perdrait son
  // horaire sans que personne l'ait demande.
  const tousCreneaux = creneaux.donnees ?? [];
  // Le creneau ne ferme qu'au PLAFOND DE SAISIE : capacite majoree de la
  // tolerance du type, arrondie a l'unite superieure (30 places a 20 %
  // en acceptent 36). Entre capacite et plafond, il reste propose,
  // marque « en tolerance ».
  const complet = (c: Creneau) =>
    c.capacite_avec_tolerance !== null && c.effectif >= c.capacite_avec_tolerance;
  const enTolerance = (c: Creneau) =>
    !complet(c) && c.capacite_max !== null && c.effectif >= c.capacite_max;
  const creneauxProposes = tousCreneaux.filter(
    (c) => !complet(c) || c.id === inscription?.creneau_id,
  );
  const creneauxMasques = tousCreneaux.length - creneauxProposes.length;

  // Prorata d'un debut en cours d'annee.
  //
  // Le calcul vit cote SERVEUR : il depend du calendrier des seances, et
  // du JOUR du creneau — au 1er mai il reste six seances le mercredi et
  // quatre le vendredi. La date proposee est la premiere seance encore a
  // venir sur ce jour-la.
  //
  // Le prorata se calcule sur le TARIF de l'activite, pas sur le montant
  // du champ : celui-ci devient le montant proratise des qu'on l'applique,
  // et le proratiser de nouveau le rabotait une seconde fois (928 EUR
  // devenaient 897). Sans tarif de reference, le montant saisi fait foi.
  // La REDUCTION part aussi (2026-10-07) : le serveur proratise le net,
  // comme la fin anticipee, et rend le montant a forcer — du plus
  // reduction — pour que les deux parts d'une annee se completent.
  const pleinTarif = activite?.tarif ?? enCentimes(montant || '0') ?? 0;
  // Les types de reduction de l'annee de la saisie, desactives compris :
  // une ligne posee sur un type desactive depuis doit rester lisible.
  const typesReduction = useRessource(
    () =>
      ouverte
        ? api.get<TypeReduction[]>('/api/types-reduction', { anneeId, inclureInactifs: true })
        : Promise.resolve([]),
    [ouverte, anneeId],
  );
  const reductionCentimes = totalReductions(lignesReduction, typesReduction.donnees ?? [], nonDetaillee);
  const prorata = useRessource(
    () =>
      debutSaisi && creneauId
        ? api.get<Prorata>('/api/inscriptions/prorata', {
            creneauId,
            date: dateDebut || undefined,
            montant: pleinTarif,
            reduction: reductionCentimes,
          })
        : Promise.resolve(null),
    [debutSaisi, creneauId, dateDebut, pleinTarif, reductionCentimes],
  );
  // Le montant du champ EST-IL deja le tarif proratise ? C'est lui qui
  // decide d'afficher le bouton ou la confirmation — y compris a la
  // reouverture d'une inscription deja proratisee.
  const proratiseApplique =
    prorata.donnees !== null &&
    prorata.donnees !== undefined &&
    enCentimes(montant || '0') === prorata.donnees.montantProratise;

  // LA DATE N'EST PLUS PRE-REMPLIE.
  //
  // Elle l'etait avec la premiere seance a venir, si bien que cocher la
  // case produisait un « demarrage differe » qui n'en etait pas un, sans
  // le moindre signal — le prorata affichait « 30 sur 30 » et personne
  // ne voyait le probleme. La premiere date possible est desormais
  // PROPOSEE sous le champ, a cliquer.

  // Forfait d'essai : 3 seances, propose seulement quand le TYPE de
  // l'activite porte l'option. La date de fin s'affiche a titre indicatif
  // — le calcul qui fait foi est refait par le serveur a l'enregistrement.
  //
  // ... OU quand l'inscription le PORTE deja (2026-10-02). Onze
  // inscriptions reprises du classeur 2026-27 ont un forfait d'essai sur
  // un type qui n'en propose pas l'option (chorale ado, chant en groupe,
  // eveil) : la case restait cachee, le tag de la liste n'avait aucune
  // explication, et enregistrer la fiche EFFACAIT le forfait en silence.
  const optionEssai =
    (activite?.option_forfait_essai ?? false) || (inscription?.forfait_essai ?? false);
  const finEssai = useRessource(
    () =>
      optionEssai && forfaitEssai && creneauId
        ? api.get<{ date_fin_essai: string | null }>('/api/inscriptions/fin-essai', {
            creneauId,
            aPartirDe: (debutSaisi && dateDebut) || undefined,
          })
        : Promise.resolve(null),
    [optionEssai, forfaitEssai, creneauId, debutSaisi, dateDebut],
  );

  const chercher = async (terme: string) => {
    setRecherche(terme);
    if (terme.length < 2) {
      setCandidats([]);
      return;
    }
    setCandidats(await api.get<Personne[]>('/api/personnes', { recherche: terme, limite: 15 }));
  };

  // Choisir un adherent, a la souris comme au clavier.
  const retenir = (candidat: Personne) => {
    setPersonne(candidat);
    setCandidats([]);
  };
  const clavier = useListeClavier(candidats, retenir);

  const soumettre = async (evenement: FormEvent) => {
    evenement.preventDefault();
    setErreur(null);

    if (!choisie) {
      setErreur('Choisissez l’adhérent à inscrire.');
      return;
    }

    // Le champ activite est saisissable : il n'a plus la validation
    // native d'un <select required>, c'est donc ici qu'on la refait.
    if (!activiteId) {
      setErreur('Choisissez l’activité.');
      return;
    }

    const base = enCentimes(montant);
    if (base === null || lignesReduction.some((l) => l.montant && enCentimes(l.montant) === null)) {
      setErreur('Montants invalides : utilisez le format 390,00.');
      return;
    }
    // Les lignes : le type, et pour une remise libre le montant et le
    // motif — le serveur fige le montant d'un type fixe lui-meme.
    const reductions = lignesReduction.map((l) => ({
      typeReductionId: l.typeReductionId,
      montant: l.montant ? (enCentimes(l.montant) ?? undefined) : undefined,
      commentaire: l.commentaire || null,
    }));

    setEnCours(true);
    try {
      if (inscription) {
        // L'activite est absente du corps : elle ne se change pas — la
        // cle etrangere a trois colonnes de `inscription` l'interdit des
        // qu'un creneau est rattache, et l'unicite (annee, personne,
        // activite) l'interdirait de toute facon.
        await api.patch(`/api/inscriptions/${inscription.id}`, {
          creneauId: creneauId || null,
          pupitre: estChorale && pupitre ? pupitre : null,
          montantBase: base,
          // La part non detaillee, telle qu'affichee — ou zero si elle a
          // ete retiree ; les lignes remplacent les lignes.
          reduction: nonDetaillee,
          reductions,
          dateDebut: debutSaisi ? dateDebut || null : null,
          forfaitEssai: optionEssai && forfaitEssai,
        });
      } else {
        await api.post('/api/inscriptions', {
          anneeId,
          personneId: choisie.id,
          activiteId,
          creneauId: creneauId || null,
          // RG-05 : le pupitre n'est transmis que pour une chorale.
          pupitre: estChorale && pupitre ? pupitre : null,
          montantBase: base,
          reductions,
          dateDebut: debutSaisi ? dateDebut || null : null,
          forfaitEssai: optionEssai && forfaitEssai,
        });
        setActiviteId('');
        setCreneauId('');
        setMontant('');
        setLignesReduction([]);
        setMontantForce(null);
        setDebutSaisi(false);
        setDateDebut('');
        setForfaitEssai(false);
      }
      onEnregistre();
    } catch (cause) {
      setErreur(cause instanceof ErreurApi ? cause.message : 'Enregistrement impossible.');
    } finally {
      setEnCours(false);
    }
  };

  return (
    <Modale
      titre={inscription ? 'Détail de l’inscription' : 'Nouvelle inscription'}
      ouverte={ouverte}
      onFermer={onFermer}
      /**
       * LE MENU SUBSTITUE, IL N'EMPILE PAS.
       *
       * Chaque action ferme cette modale avant d'ouvrir la sienne : deux
       * modales superposees partagent le meme `z-50` et posent chacune
       * son ecouteur `Escape` sur `document` — une seule touche les
       * refermerait toutes les deux. Et c'est fidele au sens : transferer
       * n'est pas modifier.
       *
       * `fermer` est la fermeture GARDEE de la modale : une saisie en
       * cours fait apparaitre la meme confirmation que la croix.
       */
      actions={(fermer) =>
        inscription && arretee ? (
          <MenuActions
            actions={
              peut('inscription.rouvrir') && onRouvrir
                ? [
                    {
                      cle: 'rouvrir',
                      libelle: 'Revenir sur la fin anticipée',
                      aide: 'Rattrapage d’une saisie erronée',
                      icone: <IconeRetour />,
                      onChoisir: () => {
                        if (
                          !window.confirm(
                            'L’inscription redeviendra active et retrouvera son montant d’avant ' +
                              'l’arrêt, réduction comprise. Continuer ?',
                          )
                        ) {
                          return;
                        }
                        fermer();
                        onRouvrir(inscription);
                      },
                    },
                  ]
                : []
            }
          />
        ) : inscription ? (
          <MenuActions
            actions={[
              ...(peut('inscription.modifier') &&
              onConfirmerEssai &&
              (inscription.essai_statut === 'en_cours' || inscription.essai_statut === 'echu')
                ? [
                    {
                      cle: 'essai',
                      libelle: 'Confirmer l’essai',
                      aide: 'L’élève poursuit : le plein tarif s’applique',
                      icone: <span aria-hidden>✓</span>,
                      onChoisir: () => {
                        fermer();
                        onConfirmerEssai(inscription);
                      },
                    },
                  ]
                : []),
              ...(peut('inscription.transferer') && onTransferer
                ? [
                    {
                      cle: 'transfert',
                      libelle: 'Transférer vers un autre créneau',
                      aide: 'Horaire, ou changement d’activité',
                      icone: <IconeTransfert />,
                      onChoisir: () => {
                        fermer();
                        onTransferer(inscription);
                      },
                    },
                  ]
                : []),
              ...(peut('inscription.cloturer') && onFinAnticipee
                ? [
                    {
                      cle: 'fin',
                      libelle: 'Déclarer une fin anticipée',
                      aide: 'Arrêt en cours d’année, au prorata',
                      icone: <IconeArret />,
                      onChoisir: () => {
                        fermer();
                        onFinAnticipee(inscription);
                      },
                    },
                  ]
                : []),
            ]}
          />
        ) : null
      }
    >
      <form onSubmit={soumettre} className="space-y-4">
        {/* LE TAG EN TETE, avant l'activite : c'est la premiere chose a
            savoir sur cette inscription, et il explique pourquoi tout le
            reste est en lecture seule. */}
        {arretee && inscription && (
          <p className="rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
            <Badge ton="alerte">Fin anticipée</Badge>{' '}
            {inscription.date_fin_anticipee && (
              <>
                dernier cours le{' '}
                <strong className="tabulaire">
                  {formaterDate(inscription.date_fin_anticipee)}
                </strong>
                .{' '}
              </>
            )}
            Le montant a été recalculé au prorata : l’inscription n’est plus modifiable. Les actes de
            gestion restent accessibles par le menu.
          </p>
        )}

        {/* `fieldset disabled` neutralise TOUS les champs qu'il contient,
            y compris ceux des composants enfants — un garde par champ en
            aurait oublie un. */}
        <fieldset disabled={arretee} className="space-y-4 disabled:opacity-60">
        {!personneImposee && !inscription && (
          <>
            <Champ libelle="Adhérent">
              <input
                className="champ"
                value={choisie ? `${choisie.nom.toUpperCase()} ${choisie.prenom}` : recherche}
                onChange={(e) => {
                  setPersonne(null);
                  void chercher(e.target.value);
                }}
                onKeyDown={clavier.auClavier}
                placeholder="Nom ou prénom…"
                autoFocus
              />
            </Champ>

            {!personne && candidats.length > 0 && (
              <ul
                ref={clavier.liste}
                className="max-h-40 divide-y divide-encre-100 overflow-y-auto rounded-md border border-encre-200"
              >
                {candidats.map((candidat, rang) => (
                  <li key={candidat.id} data-index={rang}>
                    <button
                      type="button"
                      className={`w-full px-3 py-2 text-left text-sm ${
                        rang === clavier.index ? CLASSE_SURVOL : ''
                      }`}
                      onMouseEnter={() => clavier.survoler(rang)}
                      onClick={() => retenir(candidat)}
                    >
                      {candidat.nom.toUpperCase()} {candidat.prenom}
                      <span className="ml-2 text-xs text-encre-500">
                        {formaterDate(candidat.date_naissance)}
                      </span>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </>
        )}

        {/* L'activite ne se change pas : la cle etrangere a trois colonnes
            de `inscription` l'interdit des qu'un creneau est rattache, et
            l'unicite (annee, personne, activite) l'interdirait de toute
            facon. Une autre activite, c'est une autre inscription. */}
        {inscription ? (
          <Champ
            libelle="Activité"
            aide="L’activité d’une inscription ne se change pas — inscrivez à une autre activité."
          >
            <input
              className="champ bg-encre-50 text-encre-600"
              value={`${inscription.activite_libelle} (${inscription.activite_type})`}
              readOnly
              aria-readonly="true"
            />
          </Champ>
        ) : (
          <Champ
            libelle="Activité"
            aide="L’entité de l’inscription en découle : elle n’est jamais saisie."
          >
            <ChoixActivite
              activites={activites}
              valeur={activiteId}
              onChanger={(id) => {
                setActiviteId(id);
                // Les creneaux dependent de l'activite : celui d'avant
                // n'a plus de sens.
                setCreneauId('');
                setMontantForce(null);
              }}
            />
          </Champ>
        )}

        {activiteId && (
          <Champ
            libelle="Créneau"
            aide={
              creneauxMasques > 0
                ? `Facultatif : une inscription sans créneau est acceptée mais signalée en anomalie. ${creneauxMasques} créneau(x) complet(s) ne sont pas proposés.`
                : 'Facultatif : une inscription sans créneau est acceptée mais signalée en anomalie.'
            }
          >
            <ChoixCreneau
              creneaux={creneauxProposes}
              valeur={creneauId}
              onChanger={(id) => {
                setCreneauId(id);
                // Le prorata depend du JOUR du creneau : un autre creneau
                // le rend caduc.
                setMontantForce(null);
              }}
              mention={(c) =>
                complet(c) ? '— complet' : enTolerance(c) ? '— en tolérance' : ''
              }
            />

            {/* Tous complets : le dire franchement plutot que de laisser
                une liste vide. C'est le cas courant d'un instrument, ou
                chaque creneau vaut une place — il faut en ouvrir un. */}
            {creneauxProposes.length === 0 && tousCreneaux.length > 0 && (
              <p className="mt-2 text-xs text-amber-700">
                Les {tousCreneaux.length} créneaux de cette activité sont complets. Ouvrez-en un
                depuis Enseignement → Créneaux, ou enregistrez sans créneau — l’inscription sera
                signalée en anomalie jusqu’à ce qu’elle en ait un.
              </p>
            )}
          </Champ>
        )}

        {/* RG-05 : le pupitre n'apparaît que pour une chorale. */}
        {estChorale && (
          <Champ libelle="Pupitre" aide="Voix attribuée en chorale.">
            <select className="champ" value={pupitre} onChange={(e) => setPupitre(e.target.value)}>
              <option value="">— Aucun —</option>
              <option value="S">S — Soprano</option>
              <option value="A">A — Alto</option>
              <option value="T">T — Ténor</option>
              <option value="B">B — Basse</option>
            </select>
          </Champ>
        )}

        {/* Changer le montant d'une inscription deja couverte deplace le
            rapprochement. Ce n'est pas interdit — une correction de tarif
            est legitime — mais la consequence doit se voir au moment du
            geste, pas se decouvrir dans l'ecran Paiements. */}
        {inscription && inscription.montant_regle > 0 && (
          <p className="rounded-md border border-amber-200 bg-amber-50 p-3 text-xs text-amber-800">
            Cette inscription est déjà couverte à hauteur de{' '}
            <strong className="tabulaire">{formaterMontant(inscription.montant_regle)}</strong>.
            Modifier le montant décalera d’autant le rapprochement de cet adhérent.
          </p>
        )}

        <div className="grid grid-cols-2 gap-3">
          <Champ
            libelle="Montant (€)"
            aide={
              activite?.tarif !== null && activite?.tarif !== undefined
                ? 'Tarif de l’activité. Un écart se saisit en réduction.'
                : 'Cette activité n’a pas de tarif de référence : montant à saisir.'
            }
          >
            {/* LE MONTANT EST CELUI DE L'ACTIVITE, ET NE SE TOUCHE PAS.
                Le laisser libre a produit 38 ecarts sur 192 inscriptions
                2026-27, dont des adhesions ajoutees au montant et des
                proratas saisis dans la base au lieu de la reduction. Le
                tarif de reference reste ainsi lisible (RG-02), et l'ecart
                passe par la reduction — le seul endroit qui l'explique.
                Sans tarif, il n'y a rien a suivre : le champ s'ouvre. */}
            <input
              className="champ tabulaire"
              value={montant}
              onChange={(e) => setMontant(e.target.value)}
              inputMode="decimal"
              required
              disabled={activite?.tarif !== null && activite?.tarif !== undefined}
            />
          </Champ>
          <Champ libelle="Total des réductions (€)" aide="Détaillées ci-dessous, par type.">
            <input className="champ tabulaire" value={enEuros(reductionCentimes)} readOnly disabled />
          </Champ>
        </div>

        {/* LES REDUCTIONS, PAR TYPE (2026-10-12). Elles se cumulent ; le
            montant d'un type fixe ne se touche pas, une remise libre se
            chiffre et se motive. Toute coche annule le montant force par
            le prorata : il porte sur le net de reduction (2026-10-07). */}
        <ChampReductions
          types={typesReduction.donnees ?? []}
          typeActiviteId={activite?.type_activite_id ?? null}
          lignes={lignesReduction}
          onChange={(lignes) => {
            setLignesReduction(lignes);
            setMontantForce(null);
          }}
          nonDetaillee={nonDetaillee}
          onRetirerNonDetaillee={() => {
            setNonDetaillee(0);
            setMontantForce(null);
          }}
          disabled={arretee}
        />

        {/* Début en cours d'année — le symétrique de la fin anticipée.
            Le prorata se pose en RÉDUCTION plutôt que de raboter le
            montant : le tarif de référence reste lisible, et l'écart
            s'explique de lui-même sur la fiche. */}
        <div className="rounded-md border border-encre-200 p-3">
          <label className="flex items-start gap-2 text-sm">
            <input
              type="checkbox"
              className="mt-0.5"
              checked={debutSaisi}
              // UN DEMARRAGE DIFFERE ENREGISTRE NE SE DECOCHE PLUS (2026-10-08),
              // comme le forfait d'essai : la date reste modifiable, le fait
              // d'etre arrive en cours d'annee ne se defait pas. Le serveur
              // refuse de toute facon.
              disabled={inscription?.demarrage_differe ?? false}
              onChange={(e) => {
                setDebutSaisi(e.target.checked);
                if (!e.target.checked) setDateDebut('');
                // Un prorata force n'a de sens que pour la date qui l'a
                // produit : decocher ramene au tarif plein.
                setMontantForce(null);
              }}
            />
            <span>
              Démarrage en cours d’année
              {inscription?.demarrage_differe && (
                <span className="mt-0.5 block text-xs text-encre-400">
                  Enregistré, il ne se retire plus ; la date de la première séance reste modifiable.
                </span>
              )}
            </span>
          </label>

          {debutSaisi && (
            <div className="mt-3 space-y-3">
              {!creneauId ? (
                <p className="text-xs text-amber-700">
                  Choisissez d’abord un créneau : le prorata se calcule sur les séances de son jour,
                  pas sur l’année en bloc.
                </p>
              ) : (
                <>
                  <Champ
                    libelle="Première séance suivie"
                    aide={
                      prorata.donnees?.premiere_seance
                        ? `Le cours va du ${formaterDate(prorata.donnees.premiere_seance)} au ${formaterDate(prorata.donnees.derniere_seance)}.`
                        : 'Aucun calendrier saisi pour cette année : le plein tarif s’applique.'
                    }
                  >
                    <input
                      className="champ tabulaire"
                      type="date"
                      value={dateDebut}
                      onChange={(e) => {
                        setDateDebut(e.target.value);
                        setMontantForce(null);
                      }}
                    />
                  </Champ>

                  {/* PROPOSITION, pas pre-remplissage : la premiere date
                      possible se clique. Elle disparait des qu'une date
                      est saisie — une suggestion qui reste alors qu'on a
                      choisi invite a se demander ce qu'elle vaut. */}
                  {!dateDebut && prorata.donnees?.date_proposee && (
                    <p className="text-xs text-encre-500">
                      Première date possible sur ce créneau :{' '}
                      <button
                        type="button"
                        className="underline hover:text-encre-900"
                        onClick={() => setDateDebut(prorata.donnees!.date_proposee!)}
                      >
                        {formaterDate(prorata.donnees.date_proposee)}
                      </button>
                    </p>
                  )}

                  {/* Le cas qui a produit 572 faux demarrages differes :
                      la date choisie EST le premier cours de l'annee. Le
                      dire, plutot que d'afficher « 30 sur 30 » comme si
                      de rien n'etait. Le serveur ramenera de toute facon
                      la valeur au debut normal. */}
                  {dateDebut &&
                    prorata.donnees?.premiere_seance &&
                    dateDebut <= prorata.donnees.premiere_seance && (
                      <p className="rounded-md border border-amber-200 bg-amber-50 p-3 text-xs text-amber-800">
                        Cette date est le <strong>premier cours de l’année</strong> sur ce créneau :
                        ce n’est donc pas un démarrage différé, et le tarif reste plein. Décochez la
                        case si l’élève commence à la rentrée.
                      </p>
                    )}

                  {/* LE PRORATA SE PROPOSE, IL NE S'IMPOSE PAS — et il se
                      range en REDUCTION. Le montant de base reste le
                      tarif de l'activite (RG-02) ; ce qui change quand on
                      applique, c'est le montant DU, affiche en dessous
                      pour que le geste se voie. Un prorata deja applique
                      n'est plus propose : le bouton ferait doublon. */}
                  {prorata.donnees && prorata.donnees.seancesTotales > 0 && (
                    <div className="space-y-2 rounded-md border border-encre-200 bg-encre-50 p-3 text-sm">
                      <p>
                        <span className="tabulaire font-medium">
                          {prorata.donnees.seancesDues} séances sur {prorata.donnees.seancesTotales}
                        </span>{' '}
                        <span className="text-encre-500">
                          — montant dû proratisé{' '}
                          <span className="tabulaire">{formaterMontant(prorata.donnees.montantDu)}</span>
                          {prorata.donnees.reductionSaisie > 0
                            ? ` (tarif ${formaterMontant(prorata.donnees.montantPlein)} moins ${formaterMontant(prorata.donnees.reductionSaisie)} de réduction, au prorata)`
                            : ` au lieu de ${formaterMontant(prorata.donnees.montantPlein)}`}
                        </span>
                      </p>
                      {prorata.donnees.montantProratise < prorata.donnees.montantPlein &&
                        !proratiseApplique && (
                          <>
                            {/* ROUGE : le montant du champ n'est pas le
                                montant proratise — un demarrage differe
                                enregistre au plein tarif (ABDUL HADI Acil,
                                960 EUR pour 27 seances sur 30) doit se voir
                                (2026-10-08). */}
                            <p className="text-xs font-medium text-red-700">
                              Le montant actuel ({formaterMontant(enCentimes(montant || '0') ?? 0)}) n’est pas
                              le montant proratisé.
                            </p>
                            <button
                              type="button"
                              className="bouton-danger"
                              onClick={() => setMontantForce(prorata.donnees!.montantProratise)}
                            >
                              Appliquer le prorata
                            </button>
                          </>
                        )}
                      {proratiseApplique &&
                        prorata.donnees.montantProratise < prorata.donnees.montantPlein && (
                        <p className="text-xs text-emerald-700">
                          Prorata appliqué : montant {formaterMontant(prorata.donnees.montantProratise)}
                          {prorata.donnees.reductionSaisie > 0
                            ? `, soit ${formaterMontant(prorata.donnees.montantDu)} dû après réduction.`
                            : ' dû.'}
                        </p>
                      )}
                    </div>
                  )}
                </>
              )}
            </div>
          )}
        </div>

        {/* Forfait d'essai — propose seulement quand le TYPE de
            l'activite porte l'option. Trois seances, la fin calculee sur
            le calendrier des seances du jour du creneau. */}
        {optionEssai && (
          <div className="rounded-md border border-encre-200 p-3">
            <label className="flex items-start gap-2 text-sm">
              <input
                type="checkbox"
                className="mt-0.5"
                checked={forfaitEssai}
                // UN FORFAIT ENREGISTRE NE SE DECOCHE PLUS (2026-10-08) : la
                // trace reste, on le confirme ou on arrete l'inscription.
                // Le serveur refuse de toute facon.
                disabled={inscription?.forfait_essai ?? false}
                onChange={(e) => setForfaitEssai(e.target.checked)}
              />
              <span>
                Forfait d’essai
                {inscription?.essai_statut === 'confirme' && inscription.essai_confirme_le && (
                  <span className="ml-2 rounded bg-emerald-50 px-1.5 py-0.5 text-[11px] text-emerald-700">
                    confirmé le {formaterDate(inscription.essai_confirme_le)}
                  </span>
                )}
                {inscription?.essai_statut === 'echu' && (
                  <span className="ml-2 rounded bg-red-50 px-1.5 py-0.5 text-[11px] font-medium text-red-700">
                    terminé, à confirmer
                  </span>
                )}
                <span className="mt-0.5 block text-xs text-encre-400">
                  {inscription?.forfait_essai
                    ? inscription.essai_statut === 'confirme'
                      ? 'Essai confirmé : le plein tarif s’applique.'
                      : `Enregistré, il ne se retire plus : confirmez-le quand l’élève poursuit, ou déclarez une fin anticipée.${inscription.montant_essai !== null ? ` Montant du forfait : ${formaterMontant(inscription.montant_essai)}.` : ''}`
                    : `Trois séances avant l’engagement à l’année.${activite?.tarif_essai != null ? ` Montant du forfait : ${formaterMontant(activite.tarif_essai)}.` : ' Aucun montant de forfait sur ce type : le plein tarif sera dû.'}`}
                </span>
              </span>
            </label>

            {forfaitEssai && (
              <p className="mt-2 text-xs text-encre-600">
                {!creneauId ? (
                  <span className="text-amber-700">
                    Choisissez un créneau : la fin de l’essai se calcule sur les séances de son
                    jour.
                  </span>
                ) : finEssai.donnees?.date_fin_essai ? (
                  <>
                    Fin de l’essai :{' '}
                    <span className="tabulaire font-medium">
                      {formaterDate(finEssai.donnees.date_fin_essai)}
                    </span>{' '}
                    — la 3<sup>e</sup> séance au calendrier.
                  </>
                ) : (
                  <span className="text-amber-700">
                    Calendrier des séances non saisi : la date de fin d’essai ne peut pas être
                    calculée.
                  </span>
                )}
              </p>
            )}
          </div>
        )}

        <p className="rounded-md border border-encre-200 bg-encre-50 p-3 text-xs text-encre-500">
          Montant dû ={' '}
          <span className="tabulaire font-medium">
            {formaterMontant((enCentimes(montant) ?? 0) - reductionCentimes)}
          </span>{' '}
          — calculé par la base, pas par cet écran.
        </p>

        </fieldset>

        {arretee ? (
          <div className="flex justify-end">
            <button type="button" className="bouton-secondaire" onClick={onFermer}>
              Fermer
            </button>
          </div>
        ) : (
          <PiedFormulaire
            erreur={erreur}
            enCours={enCours}
            onFermer={onFermer}
            libelle={inscription ? 'Enregistrer' : 'Inscrire'}
          />
        )}
      </form>
    </Modale>
  );
}

// --- Fin anticipée ----------------------------------------------------

/** D'ou vient le montant du apres arret (2026-10-09). */
type BaseFinAnticipee = 'prorata' | 'forfait' | 'inchange';

export function FormulaireFinAnticipee({
  inscription,
  onFermer,
  onEnregistre,
}: {
  inscription: Inscription | null;
  onFermer: () => void;
  onEnregistre: () => void;
}) {
  const { peut } = useAuth();
  const aujourdHui = new Intl.DateTimeFormat('fr-CA', { timeZone: 'Europe/Paris' }).format(new Date());
  // La date n'est PAS pre-remplie, sauf pour un forfait d'essai : le
  // dernier cours suivi est une information que seul l'utilisateur
  // detient, et une date du jour proposee d'office serait choisie par
  // defaut sans etre verifiee. L'essai, lui, a une fin connue du systeme.
  const [date, setDate] = useState('');
  const [montant, setMontant] = useState('');
  const [base, setBase] = useState<BaseFinAnticipee>('prorata');
  const [rembourser, setRembourser] = useState(false);
  const [montantRembourse, setMontantRembourse] = useState('');
  const [dateVirement, setDateVirement] = useState(aujourdHui);
  const [erreur, setErreur] = useState<string | null>(null);
  const [enCours, setEnCours] = useState(false);
  const [initialisePour, setInitialisePour] = useState<string | null>(null);

  /**
   * LE CALCUL VIENT DU SERVEUR, pas de l'ecran.
   *
   * Il depend du calendrier, du jour du creneau et de la date de debut
   * de l'inscription — un demarrage differe ne compte que les seances
   * qui le suivent. Le refaire ici dupliquerait une regle d'argent.
   */
  const simulation = useRessource(
    () =>
      inscription
        ? api.get<SimulationFinAnticipee>(`/api/inscriptions/${inscription.id}/fin-anticipee`, {
            date: date || undefined,
          })
        : Promise.resolve(null),
    [inscription?.id, date],
  );
  const sim = simulation.donnees;

  // TROIS BRANCHES (2026-10-09), une seule proposee selon le cas :
  //   - prorata des seances (le cas general) ;
  //   - forfait d'essai : SEULE branche d'un essai non confirme — l'eleve
  //     a eu son essai, il paie l'essai, le prorata n'a pas de sens ;
  //   - montant inchange : l'eleve part, l'ecole garde l'engagement.
  // Le montant reste un champ quelle que soit la branche (RG-03 : un
  // arbitrage, pas un calcul impose).
  const essai = !!sim?.forfait_essai_du;
  const montantDeLaBase = (b: BaseFinAnticipee): number | null => {
    if (!sim) return null;
    if (b === 'forfait') return sim.montant_forfait;
    if (b === 'inchange') return sim.montant_plein;
    return date ? sim.montant_proratise : null;
  };

  // Le montant propose suit la branche tant que l'utilisateur n'a rien
  // saisi de son cote.
  const [montantTouche, setMontantTouche] = useState(false);
  const propose = montantDeLaBase(base);
  if (!montantTouche && propose !== null && montant !== enEuros(propose)) {
    setMontant(enEuros(propose));
  }

  if (inscription && inscription.id !== initialisePour) {
    setInitialisePour(inscription.id);
    setMontantTouche(false);
    setDate('');
    setMontant('');
    setBase('prorata');
    setRembourser(false);
    setMontantRembourse('');
    setDateVirement(aujourdHui);
    setErreur(null);
  }
  if (!inscription && initialisePour !== null) setInitialisePour(null);

  // Un essai non confirme : la branche est le forfait, et la date se cale
  // d'office sur la fin de l'essai (modifiable — partir apres deux seances
  // sur trois ne change pas ce qui est du).
  if (sim && essai && base !== 'forfait') setBase('forfait');
  if (sim && essai && !date && sim.date_fin_essai && sim.dates.includes(sim.date_fin_essai)) {
    setDate(sim.date_fin_essai);
  }

  // LE TROP-PERCU : ce que l'inscription a recu moins ce qu'elle doit
  // apres arret. Positif, un remboursement se propose — a la Direction.
  const du = enCentimes(montant);
  const tropPercu = sim && du !== null ? Math.max(0, sim.montant_regle - du) : 0;
  const peutRembourser = peut('reglement.rembourser');
  if (rembourser && tropPercu > 0 && montantRembourse === '') setMontantRembourse(enEuros(tropPercu));

  const soumettre = async (evenement: FormEvent) => {
    evenement.preventDefault();
    if (!inscription) return;

    const recalcule = enCentimes(montant);
    if (recalcule === null) {
      setErreur('Montant invalide : utilisez le format 390,00.');
      return;
    }
    let remboursement: { montant: number; dateVirement: string } | undefined;
    if (rembourser && tropPercu > 0) {
      const rendu = enCentimes(montantRembourse);
      if (rendu === null || rendu <= 0 || rendu > tropPercu) {
        setErreur(`Montant à rembourser invalide : au plus ${formaterMontant(tropPercu)}.`);
        return;
      }
      remboursement = { montant: rendu, dateVirement };
    }

    setErreur(null);
    setEnCours(true);
    try {
      await api.post(`/api/inscriptions/${inscription.id}/fin-anticipee`, {
        date,
        montantRecalcule: recalcule,
        base,
        remboursement,
      });
      onEnregistre();
    } catch (cause) {
      setErreur(cause instanceof ErreurApi ? cause.message : 'Opération impossible.');
    } finally {
      setEnCours(false);
    }
  };

  const choixBase = (b: BaseFinAnticipee, libelle: string, aide: string) => (
    <label className="flex cursor-pointer items-start gap-2 text-sm">
      <input
        type="radio"
        name="base-fin-anticipee"
        className="mt-1"
        checked={base === b}
        onChange={() => {
          setBase(b);
          setMontantTouche(false);
        }}
      />
      <span>
        {libelle}
        <span className="block text-xs text-encre-500">{aide}</span>
      </span>
    </label>
  );

  return (
    <Modale titre="Déclarer une fin anticipée" ouverte={Boolean(inscription)} onFermer={onFermer}>
      {inscription && (
        <form onSubmit={soumettre} className="space-y-4">
          <p className="text-sm text-encre-600">
            {inscription.personne_nom.toUpperCase()} {inscription.personne_prenom} —{' '}
            {inscription.activite_libelle}
          </p>

          <Champ
            libelle="Dernier cours suivi"
            aide={
              essai
                ? 'Calé sur la fin du forfait d’essai ; seules les dates de cours sont sélectionnables.'
                : 'Seules les dates de cours de ce créneau sont sélectionnables.'
            }
          >
            {sim && sim.dates.length > 0 ? (
              <ChoixSeance
                dates={sim.dates}
                valeur={date}
                onChanger={(d) => {
                  setDate(d);
                  // Changer de date repose la proposition : le montant
                  // saisi pour une autre date n'a plus de sens.
                  setMontantTouche(false);
                }}
              />
            ) : (
              <p className="text-xs text-amber-700">
                Aucun calendrier saisi pour ce créneau : le prorata ne peut pas se calculer, le
                montant reste à saisir à la main.
              </p>
            )}
          </Champ>

          <fieldset className="space-y-2">
            <legend className="etiquette">Montant dû après arrêt</legend>
            {essai
              ? choixBase(
                  'forfait',
                  `Forfait d’essai — ${formaterMontant(sim?.montant_forfait ?? null)}`,
                  'L’essai n’a pas été confirmé : l’élève doit le forfait, pas un prorata des séances.',
                )
              : choixBase(
                  'prorata',
                  'Prorata des séances suivies',
                  date && sim && sim.seances_totales > 0
                    ? `${sim.seances_faites} séance${sim.seances_faites > 1 ? 's' : ''} sur ${sim.seances_totales} du ${formaterDate(sim.date_debut ?? '')} au ${formaterDate(date)} : ${formaterMontant(sim.montant_plein)} → ${formaterMontant(sim.montant_proratise)}`
                    : 'Choisissez le dernier cours pour voir le décompte.',
                )}
            {choixBase(
              'inchange',
              `Montant inchangé — ${formaterMontant(sim?.montant_plein ?? null)}`,
              'L’élève arrête, l’école garde l’engagement : rien n’est recalculé.',
            )}
            {!essai && sim && sim.reduction_existante > 0 && base === 'prorata' && (
              <p className="text-xs text-encre-500">
                Réduction de <span className="tabulaire">{formaterMontant(sim.reduction_existante)}</span> déjà
                déduite : elle est conservée, et le prorata s’y applique.
              </p>
            )}
          </fieldset>

          <Champ
            libelle="Montant dû après arrêt (€)"
            aide="Proposé selon la branche choisie. Un arbitrage : ajustez si besoin."
          >
            <input
              className="champ tabulaire"
              value={montant}
              onChange={(e) => {
                setMontantTouche(true);
                setMontant(e.target.value);
              }}
              inputMode="decimal"
              required
            />
          </Champ>

          {/* LE TROP-PERCU (2026-10-09) : l'inscription a recu plus que son
              nouveau du. La Direction peut rendre la difference dans la
              foulee — un virement, enregistre comme un reglement negatif,
              prevu tant que sa date n'est pas passee. */}
          {sim && tropPercu > 0 && (
            <div className="space-y-2 rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
              <p>
                Déjà reçu <span className="tabulaire">{formaterMontant(sim.montant_regle)}</span> pour{' '}
                <span className="tabulaire">{formaterMontant(du)}</span> dû :{' '}
                <strong className="tabulaire">{formaterMontant(tropPercu)} de trop-perçu</strong>.
              </p>
              {peutRembourser ? (
                <>
                  <label className="flex items-center gap-2">
                    <input type="checkbox" checked={rembourser} onChange={(e) => setRembourser(e.target.checked)} />
                    Rembourser par virement
                  </label>
                  {rembourser && (
                    <div className="grid grid-cols-2 gap-3">
                      <Champ libelle="Montant remboursé (€)">
                        <input
                          className="champ tabulaire"
                          value={montantRembourse}
                          onChange={(e) => setMontantRembourse(e.target.value)}
                          inputMode="decimal"
                          required
                        />
                      </Champ>
                      <Champ libelle="Date du virement" aide="Future : prévu jusque-là.">
                        <input
                          className="champ"
                          type="date"
                          value={dateVirement}
                          onChange={(e) => setDateVirement(e.target.value)}
                          required
                        />
                      </Champ>
                    </div>
                  )}
                </>
              ) : (
                <p className="text-xs">Le remboursement se saisit par la Direction, depuis la fiche adhérent.</p>
              )}
            </div>
          )}

          <p className="rounded-md border border-amber-200 bg-amber-50 p-3 text-xs text-amber-900">
            L’inscription sort des effectifs actifs mais{' '}
            <strong>reste comptabilisée dans le chiffre d’affaires</strong>, et conserve son créneau
            pour l’historique.
          </p>

          <PiedFormulaire
            erreur={erreur}
            enCours={enCours}
            onFermer={onFermer}
            libelle="Déclarer"
          />
        </form>
      )}
    </Modale>
  );
}
